'use client';

import { useState, useEffect, useCallback } from 'react';

interface UseBrandColor {
  brandColor: string;
  setBrandColor: (color: string) => Promise<void>;
  resetBrandColor: () => void;
}

// Custom event names for brand color changes
const BRAND_COLOR_UPDATED_EVENT = 'brandColorUpdated';
const BRAND_COLOR_RESET_EVENT = 'brandColorReset';

// Default brand color (current gray-100)
const DEFAULT_BRAND_COLOR = '#f3f4f6';

// Predefined color options
export const BRAND_COLOR_OPTIONS = [
  { name: 'Default Gray', value: '#f3f4f6' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Slate', value: '#64748b' },
];

export function useBrandColor(): UseBrandColor {
  const [brandColor, setBrandColorState] = useState<string>(DEFAULT_BRAND_COLOR);

  // Load brand color from localStorage
  const loadBrandColor = useCallback(() => {
    const savedColor = localStorage.getItem('brandColor');
    setBrandColorState(savedColor || DEFAULT_BRAND_COLOR);
  }, []);

  useEffect(() => {
    // Load brand color from localStorage on mount
    loadBrandColor();

    // Listen for brand color change events from other components
    const handleBrandColorUpdated = (event: CustomEvent) => {
      setBrandColorState(event.detail.color);
    };

    const handleBrandColorReset = () => {
      setBrandColorState(DEFAULT_BRAND_COLOR);
    };

    window.addEventListener(BRAND_COLOR_UPDATED_EVENT, handleBrandColorUpdated as EventListener);
    window.addEventListener(BRAND_COLOR_RESET_EVENT, handleBrandColorReset);

    return () => {
      window.removeEventListener(
        BRAND_COLOR_UPDATED_EVENT,
        handleBrandColorUpdated as EventListener,
      );
      window.removeEventListener(BRAND_COLOR_RESET_EVENT, handleBrandColorReset);
    };
  }, [loadBrandColor]);

  const setBrandColor = async (color: string) => {
    // Update local state
    setBrandColorState(color);

    // Update localStorage
    localStorage.setItem('brandColor', color);

    // Dispatch event to notify other components
    window.dispatchEvent(
      new CustomEvent(BRAND_COLOR_UPDATED_EVENT, {
        detail: { color },
      }),
    );
  };

  const resetBrandColor = () => {
    // Update local state
    setBrandColorState(DEFAULT_BRAND_COLOR);

    // Update localStorage
    localStorage.removeItem('brandColor');

    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent(BRAND_COLOR_RESET_EVENT));
  };

  return {
    brandColor,
    setBrandColor,
    resetBrandColor,
  };
}
